/**
 * Parser for the structured 422 / 429 / 403 / 404 / 500 responses from
 * /group/presets/ and /group/admin/presets/. callApi throws AxiosError-shaped
 * errors; the body lives at err.response.data.detail (FastAPI's HTTPException
 * contract).
 *
 * Mirrors my-pymble-portal/src/components/presets/presetErrorParser.ts and
 * adds Phase 2 codes:
 *
 *   - manager_contact_ids_must_be_empty (V5 - flip shared->personal with
 *     managers attached, surfaces `current_manager_count`)
 *   - managers_require_shared_audience  (V1)
 *   - managers_cannot_edit_managers     (V6 - update by manager-only caller)
 *   - managers_cannot_delete            (V7)
 *   - owner_must_be_active              (V8 - admin create-on-behalf)
 *   - audience_mismatch                 (admin create with audience != shared)
 *
 * The list is permissive: anything not in ERROR_TITLES falls through to a
 * generic title and the bullet builder still extracts any present id-list
 * fields so the operator sees what broke.
 */

export interface PresetErrorSummary {
  title: string;
  bullets: string[];
}

interface ApiErrorLike {
  response?: {
    status?: number;
    data?: {
      detail?:
        | {
            error?: string;
            invalid_constituent_ids?: string[];
            invalid_contact_ids?: string[];
            invalid_manager_ids?: number[];
            missing_constituent_ids?: string[];
            missing_contact_ids?: string[];
            missing_manager_ids?: number[];
            unauthorised_constituent_ids?: string[];
            unauthorised_contact_ids?: string[];
            unauthorised_manager_ids?: number[];
            nested_constituent_ids?: string[];
            submitted?: number | string;
            max_allowed?: number;
            retry_after_seconds?: number;
            current_manager_count?: number;
            owner_contact_id?: number;
          }
        | string;
    };
  };
  message?: string;
}

// Codes are the canonical strings apis#796 actually returns. The pre-existing
// aliases (managers_require_shared_audience, managers_cannot_edit_managers,
// managers_cannot_delete, owner_must_be_active) are kept so any in-flight UI
// path that still sends the old name (or a cached error body) renders the
// right title; new code should use the apis-canonical names. Codex round 1
// Med-3 caught the parser ignoring V7/V8/admin-invalid codes - everything
// fell through to the generic "Your changes could not be saved" message.
const ERROR_TITLES: Record<string, string> = {
  preset_constituent_invalid_or_inaccessible:
    "One or more selections could not be added",
  invalid_or_inaccessible: "One or more selections could not be added",
  preset_cannot_contain_preset: "A preset cannot contain another preset",
  preset_constituent_not_found: "One or more selections could not be found",
  preset_constituent_not_visible:
    "One or more selections are not visible to you",
  too_many_constituent_groups: "Too many constituent groups",
  too_many_individual_contacts: "Too many individual contacts",
  too_many_managers: "Too many managers",
  invalid_id_format: "An id was in an unexpected format",
  shared_presets_not_yet_available: "Shared presets are not yet available",
  rate_limited: "Refresh limit reached",
  preset_not_found: "Preset not found",
  forbidden: "You don't have access to this preset",
  // V1: create + update reject managers on personal audience.
  manager_contact_ids_requires_shared:
    "Managers can only be set on shared presets",
  managers_require_shared_audience:
    "Managers can only be set on shared presets",
  // V2: managers must be in the caller's visible-contact set.
  manager_contact_ids_not_visible:
    "One or more managers are not visible to you",
  // V6: manager-only callers cannot edit the manager list.
  manager_cannot_edit_manager_list:
    "Managers can edit a preset but cannot add or remove other managers",
  managers_cannot_edit_managers:
    "Managers can edit a preset but cannot add or remove other managers",
  // V7: manager-only callers cannot delete.
  manager_cannot_delete: "Managers cannot delete a preset",
  managers_cannot_delete: "Managers cannot delete a preset",
  // V5: flip shared->personal with managers attached.
  manager_contact_ids_must_be_empty:
    "Remove all managers before flipping this preset to personal",
  // V8: admin create-on-behalf with inactive/missing owner.
  owner_contact_invalid_or_inactive:
    "The selected owner is not an active contact - choose someone else",
  owner_must_be_active:
    "The selected owner is not an active contact - choose someone else",
  // Admin-bypass branch existence check (apis#796 round-1 Med-2).
  manager_contact_ids_invalid_or_inactive:
    "One or more managers are not active contacts",
  audience_mismatch:
    "Admin-created presets must be shared - personal is not allowed here",
  // Companion parent group (apis#1135 validation helpers). Derivation does not
  // chain, and one source preset carries at most one companion.
  preset_already_has_companion:
    "This preset already has a companion parent group",
  companion_cannot_have_companion:
    "A parent group is already derived - it cannot have its own parent group",
  companion_definition_read_only:
    "A parent group's members are computed and cannot be edited by hand",
};

/**
 * Extract a structured preset summary from any error thrown by callApi.
 * Returns null if the error doesn't look like a preset-API response (caller
 * can fall back to a generic toast).
 */
export function parsePresetError(err: unknown): PresetErrorSummary | null {
  const apiErr = err as ApiErrorLike;
  const status = apiErr?.response?.status;
  const detail = apiErr?.response?.data?.detail;

  if (typeof detail === "string") {
    if (status === 401 || status === 403 || status === 404) {
      return { title: detail, bullets: [] };
    }
    return null;
  }

  if (!detail || typeof detail !== "object") {
    return null;
  }

  if (typeof status === "number" && status >= 500) {
    return null;
  }

  const code = detail.error ?? "";
  const title = ERROR_TITLES[code] ?? "Your changes could not be saved";

  const bullets: string[] = [];

  if (detail.nested_constituent_ids?.length) {
    bullets.push(
      `${detail.nested_constituent_ids.length} item(s) are themselves presets`
    );
  }
  if (detail.invalid_constituent_ids?.length) {
    bullets.push(
      `${detail.invalid_constituent_ids.length} group(s) could not be added`
    );
  }
  if (detail.invalid_contact_ids?.length) {
    bullets.push(
      `${detail.invalid_contact_ids.length} contact(s) could not be added`
    );
  }
  if (detail.invalid_manager_ids?.length) {
    bullets.push(
      `${detail.invalid_manager_ids.length} manager(s) could not be added`
    );
  }
  if (detail.missing_constituent_ids?.length) {
    bullets.push(
      `${detail.missing_constituent_ids.length} group(s) could not be found`
    );
  }
  if (detail.missing_contact_ids?.length) {
    bullets.push(
      `${detail.missing_contact_ids.length} contact(s) could not be found`
    );
  }
  if (detail.missing_manager_ids?.length) {
    bullets.push(
      `${detail.missing_manager_ids.length} manager(s) could not be found`
    );
  }
  if (detail.unauthorised_constituent_ids?.length) {
    bullets.push(
      `${detail.unauthorised_constituent_ids.length} group(s) are not visible to you`
    );
  }
  if (detail.unauthorised_contact_ids?.length) {
    bullets.push(
      `${detail.unauthorised_contact_ids.length} contact(s) are not visible to you`
    );
  }
  if (detail.unauthorised_manager_ids?.length) {
    bullets.push(
      `${detail.unauthorised_manager_ids.length} manager(s) are not visible to you`
    );
  }
  if (typeof detail.submitted === "number" && detail.max_allowed) {
    bullets.push(
      `You submitted ${detail.submitted}; maximum is ${detail.max_allowed}`
    );
  }
  if (typeof detail.retry_after_seconds === "number") {
    bullets.push(`Try again in ${detail.retry_after_seconds} seconds`);
  }
  if (
    code === "manager_contact_ids_must_be_empty" &&
    typeof detail.current_manager_count === "number"
  ) {
    bullets.push(
      `${detail.current_manager_count} manager(s) are currently attached`
    );
  }
  if (
    (code === "owner_contact_invalid_or_inactive" ||
      code === "owner_must_be_active") &&
    typeof detail.owner_contact_id === "number"
  ) {
    bullets.push(`Inactive contact id: ${detail.owner_contact_id}`);
  }

  return { title, bullets };
}
